import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import type { TrpcClient } from '@client/hooks/api/useTrpc';
import type { DurationKey } from '@client/types/trainingRecord';
import { downloadBlob } from '@client/utils';
import type { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';

const REPORT_FIELDS = [
    'EmployeeID',
    'EmployeeName',
    'TrainingName',
    'TrainingType',
    'TrainingProvider',
    'DateCompleted',
    'NumberOfHours',
    'CPDPoints',
] as const;

type CsvRow = {
    [key in (typeof REPORT_FIELDS)[number] | string]: string | undefined | null;
};

export async function exportStaffTrainingRecords(trpcClient: TrpcClient, userId: bigint) {
    try {
        message.loading('Generating file. Please wait...', 0);

        const data = await trpcClient.trainingRecord.listTrainingRecords.query({
            where: {
                userId: userId,
            },
            limit: 1000, // Set a high limit to get all records for export
            offset: 0,
        });

        const csvRows = data.rows.map((tr) => {
            return {
                EmployeeID: tr.User?.id,
                EmployeeName: tr.User?.fullName,
                TrainingName: tr.trainingName,
                TrainingType: tr.type,
                TrainingProvider: tr.provider,
                DateCompleted: tr.dateOfTraining,
                NumberOfHours: tr.noOfHours,
                CPDPoints: tr.cipCredits,
            } as CsvRow;
        });

        //replace long dashes (excel doesn't display correctly)
        const { parse } = await import('json2csv');
        let csv = parse(csvRows, { fields: Object.assign(REPORT_FIELDS) });
        csv = csv.replace(/[\u2013\u2014]/gm, '-');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        downloadBlob(blob, `training-records-user-id-${userId}-${new Date().toISOString()}.csv`);
        message.success('File saved.');
    } catch (e) {
        message.destroy();
        errorMessage.show(e);
    }
}

type ModifyDateFunction = (date: Date) => void;

const durationMappings: Record<DurationKey, ModifyDateFunction> = {
    '1_week': (date) => date.setDate(date.getDate() + 7),
    '2_weeks': (date) => date.setDate(date.getDate() + 14),
    '1_month': (date) => date.setMonth(date.getMonth() + 1),
    '2_months': (date) => date.setMonth(date.getMonth() + 2),
    '3_months': (date) => date.setMonth(date.getMonth() + 3),
    '6_months': (date) => date.setMonth(date.getMonth() + 6),
    '1_year': (date) => date.setFullYear(date.getFullYear() + 1),
};

export function addDuration(startDate: Date, durationKey: DurationKey) {
    const date = new Date(startDate);
    const modifyDate = durationMappings[durationKey];
    if (modifyDate) {
        modifyDate(date);
    }
    date.setDate(date.getDate() - 1);
    return new Date(date);
}

export function isTrainingPeriodCurrent(dateFrom: Date, dateTo: Date) {
    const now = DateTime.now();
    const from = DateTime.fromJSDate(dateFrom);
    const to = DateTime.fromJSDate(dateTo);
    return from <= now && now <= to;
}

export function formatTrainingRecordDecimal(value: Decimal | number | null | undefined) {
    const PRECISION = 2;
    const [integer, fraction] = (value ?? 0).toString().split('.');
    const fractionChunk = fraction?.slice(0, PRECISION) || '';

    return `${integer}.${fractionChunk}${'0'.repeat(PRECISION - fractionChunk.length)}`;
}
